import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import type { APIPersonalAutoApplicationForm } from 'types/api.types';

interface ReportHeaderProps {
  applicationData?: APIPersonalAutoApplicationForm | null;
}

const ReportHeader = ({ applicationData }: ReportHeaderProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const headerBgColor = useColorModeValue('gray.50', 'navy.900');

  const ValueDisplay = ({ value }: { value: string }) => {
    if (value === '(Missing)') {
      return (
        <Badge colorScheme="red" fontSize="sm" fontWeight="bold">
          Missing
        </Badge>
      );
    }
    return (
      <Text fontWeight="bold" color={textColor} fontSize="sm">
        {value}
      </Text>
    );
  };

  // Helper function to handle missing vs empty values
  const formatValue = (value: string | undefined | null) => {
    // If the path doesn't exist in the data structure
    if (value === undefined) {
      return '(Missing)';
    }
    // If the value exists but is empty or null
    if (value === null || value === '') {
      return '-';
    }
    return value;
  };

  const getFullName = () => {
    const firstName = applicationData?.applicant?.first_name || '';
    const middleName = applicationData?.applicant?.middle_name || '';
    const lastName = applicationData?.applicant?.last_name || '';
    return [firstName, middleName, lastName].filter(Boolean).join(' ');
  };

  const headerData = {
    // From company_and_broker_information section
    insuranceCompany: formatValue(
      applicationData?.company_and_broker_information?.insurance_company,
    ),
    brokerName: formatValue(
      applicationData?.company_and_broker_information?.broker,
    ),
    branchName: formatValue(
      applicationData?.company_and_broker_information?.broker_branch,
    ),
    brokerCode: formatValue(
      applicationData?.company_and_broker_information?.broker_code,
    ),

    // From policy_information section
    binderNumber: formatValue(
      applicationData?.policy_information?.policy_no_assigned,
    ),

    // From applicant section
    customerName: getFullName(),

    // Customer code - not found in standard application form structure
    // This might need to come from a different field or be generated separately
    customerCode: '(Missing)',

    // Report date (generated)
    reportDate: new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderTopRadius="md"
      p={6}
      bg={headerBgColor}
    >
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Insurer
          </Text>
          <ValueDisplay value={headerData.insuranceCompany} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Report Date
          </Text>
          <ValueDisplay value={headerData.reportDate} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Broker Name
          </Text>
          <ValueDisplay value={headerData.brokerName} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Branch Name
          </Text>
          <ValueDisplay value={headerData.branchName} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Binder Number
          </Text>
          <ValueDisplay value={headerData.binderNumber} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Broker Code
          </Text>
          <ValueDisplay value={headerData.brokerCode} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Customer Name
          </Text>
          <ValueDisplay value={headerData.customerName} />
        </Box>
        <Box>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Customer Code
          </Text>
          <ValueDisplay value={headerData.customerCode} />
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default ReportHeader;
